import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ParseIsoDate } from 'agu_app_shell';
import { CanonicalEmploymentType } from '../../finder/employment-type';
import { ResearchJobMatch } from '../../models/research.models';
import { CompanyTypeLabel, Initials } from './job-feed-fields';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** "$" for USD (or unstated), otherwise "{CURRENCY} " — shared by every money formatter below so there's exactly one currency-prefix rule. */
function CurrencyPrefix(currency: string | null): string {
    return !currency || currency === 'USD' ? '$' : `${currency} `;
}

/** "$118K" / "$1.2M" / "$800" — compact money, no cents. USD unless a non-USD currency is stated. */
function FormatMoneyShort(amount: number, currency: string | null): string {
    const prefix = CurrencyPrefix(currency);
    if (amount >= 1_000_000) {
        const millions = amount / 1_000_000;
        return `${prefix}${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
    }
    if (amount >= 1_000) {
        return `${prefix}${Math.round(amount / 1_000)}K`;
    }
    return `${prefix}${amount}`;
}

/** global-constraints "Pay multipliers" — annual ÷ multiplier = the posted-period amount. YEAR (and null) skip this path entirely and keep `FormatMoneyShort`. */
const PAY_PERIOD_DIVISOR: Record<'HOUR' | 'DAY' | 'WEEK' | 'MONTH', number> = {
    HOUR: 2080,
    DAY: 260,
    WEEK: 52,
    MONTH: 12,
};

/** global-constraints "Pay text" suffix table. */
const PAY_PERIOD_SUFFIX: Record<'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR', string> = {
    HOUR: '/hr',
    DAY: '/day',
    WEEK: '/wk',
    MONTH: '/mo',
    YEAR: '/yr',
};

/** "$50" / "$36.75" — HOUR amount, up to 2 decimals with trailing zeros trimmed (annual ÷ 2080, rounded to 2 decimals first). */
function FormatHourlyAmount(amount: number, currency: string | null): string {
    const hourly = Math.round((amount / PAY_PERIOD_DIVISOR.HOUR) * 100) / 100;
    const trimmed = hourly % 1 === 0 ? hourly.toString() : hourly.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return `${CurrencyPrefix(currency)}${trimmed}`;
}

/** "$400" / "$1,500" / "$6,000" — DAY/WEEK/MONTH amount, whole with thousands separators (annual ÷ the period's multiplier). */
function FormatWholePeriodAmount(amount: number, currency: string | null, period: 'DAY' | 'WEEK' | 'MONTH'): string {
    const value = Math.round(amount / PAY_PERIOD_DIVISOR[period]);
    return `${CurrencyPrefix(currency)}${value.toLocaleString('en-US')}`;
}

/** The posted-period amount for one annual figure, formatted per global-constraints' "Pay text" table. YEAR/null reuse the existing `FormatMoneyShort`. */
function FormatPayAmount(amount: number, currency: string | null, period: 'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' | null): string {
    if (period === 'HOUR') return FormatHourlyAmount(amount, currency);
    if (period === 'DAY' || period === 'WEEK' || period === 'MONTH') return FormatWholePeriodAmount(amount, currency, period);
    return FormatMoneyShort(amount, currency);
}

/**
 * "$118K – $142K" (both bounds stated and differ) / "Up to $142K" / "From
 * $118K" / "Salary not stated", with the period suffix (`/hr`, `/day`,
 * `/wk`, `/mo`, `/yr`) appended to whichever shape results — `salaryPeriod`
 * null behaves exactly like `YEAR` (spec D7, Review Focus 3). Pure so
 * `JobDetailComponent` can reuse it for its own Salary fact tile rather than
 * re-deriving the same cases (mirrors `FormatAwardLine`, grant-card.component.ts).
 */
export function FormatSalaryLine(job: Pick<ResearchJobMatch, 'salaryMinAnnual' | 'salaryMaxAnnual' | 'salaryCurrency' | 'salaryPeriod'>): string {
    const { salaryMinAnnual, salaryMaxAnnual, salaryCurrency, salaryPeriod } = job;
    const suffix = PAY_PERIOD_SUFFIX[salaryPeriod ?? 'YEAR'];
    if (salaryMinAnnual !== null && salaryMaxAnnual !== null && salaryMinAnnual !== salaryMaxAnnual) {
        return `${FormatPayAmount(salaryMinAnnual, salaryCurrency, salaryPeriod)} – ${FormatPayAmount(salaryMaxAnnual, salaryCurrency, salaryPeriod)}${suffix}`;
    }
    if (salaryMaxAnnual !== null) return `Up to ${FormatPayAmount(salaryMaxAnnual, salaryCurrency, salaryPeriod)}${suffix}`;
    if (salaryMinAnnual !== null) return `From ${FormatPayAmount(salaryMinAnnual, salaryCurrency, salaryPeriod)}${suffix}`;
    return 'Salary not stated';
}

/**
 * "2d ago" / "1d ago" — the mockup's compact posted age
 * (`j.days===1?'1d ago':j.days+'d ago'`, jobs.html:615 and :631), or null when
 * the posting carries no date. `now` is injectable for tests; defaults to the
 * real clock.
 *
 * `datePosted` is NOT a bare `YYYY-MM-DD` date — `Jobs.JobPosting.DatePosted`
 * is a SQL `DATETIMEOFFSET`, serialised via `Date.prototype.toISOString()`
 * in `apps/MJAPI/src/resolvers/jobs/jobHydration.ts`'s `isoOrNull`, so it
 * always arrives as a full ISO datetime. `ParseIsoDate` (agu_app_shell)
 * handles both shapes and returns `null` (never `Invalid Date`) for a
 * missing or unparseable value — appending a second `T00:00:00Z` to an
 * already-complete ISO string, the pre-fix bug here, produced "posted NaN
 * days ago" for every job (review finding, 2026-09-03).
 */
export function FormatJobAge(job: Pick<ResearchJobMatch, 'datePosted'>, now: Date = new Date()): string | null {
    const posted = ParseIsoDate(job.datePosted);
    if (!posted) return null;
    // Whole CALENDAR days (the posting's UTC day, the member's LOCAL today), never an instant difference: a job
    // posted the day before yesterday read "3d ago" from noon onward once the
    // 2.5-day gap rounded up. Same lesson `DaysUntilClose` (grant-card) learned
    // about a deadline read against a wall-clock `now`.
    const postedDay = Date.UTC(posted.getUTCFullYear(), posted.getUTCMonth(), posted.getUTCDate());
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.max(0, Math.round((today - postedDay) / MILLISECONDS_PER_DAY));
    return `${days}d ago`;
}

/** The Posted fact tile's value (jobs.html:631): the compact age, or the honest fallback when the posting carries no date — the mockup's sample always has one (D-F6). */
export function FormatJobPostedFact(job: Pick<ResearchJobMatch, 'datePosted'>, now: Date = new Date()): string {
    return FormatJobAge(job, now) ?? 'Posted date not stated';
}

/**
 * How `FormatJobMeta` ends the line. The mockups use all three shapes: the job
 * card's `.jorg` is `{org} · 2d ago` (jobs.html:615), Home's pick row is
 * `… · posted 2d ago` (home.html:561), and Job Detail's subtitle omits the age
 * because Posted is already its own fact tile there.
 */
export type JobMetaPosted = 'none' | 'compact' | 'prefixed';

export interface FormatJobMetaOptions {
    posted: JobMetaPosted;
}

/**
 * The posted segment for a given shape: null for 'none'; with no date, the honest empty `Posted date not stated`
 * (E-17, B-13, I-5 — D-F6), so the card, Home's pick row and the Search row always carry the segment.
 */
function PostedSegment(job: Pick<ResearchJobMatch, 'datePosted'>, posted: JobMetaPosted, now: Date): string | null {
    if (posted === 'none') return null;
    const age = FormatJobAge(job, now);
    if (!age) return FormatJobPostedFact(job, now);
    return posted === 'prefixed' ? `posted ${age}` : age;
}

/**
 * "Atmospheric Sciences Division · Boulder, CO · 2d ago" — omits a company or
 * location that is null; the posted segment reads `Posted date not stated` without a date. Shared by `JobCardComponent` and the Saved board that
 * reuses it (`posted: 'compact'`, the mockup's `.jorg`), Home's pick rows
 * (`'prefixed'`), Search rows (`'compact'`) and `JobDetailComponent`
 * (`'none'` — Posted is already its own fact tile there, so the subtitle would
 * otherwise repeat it), so the company/location join logic exists in exactly
 * one place (review finding, 2026-09-03).
 */
export function FormatJobMeta(
    job: Pick<ResearchJobMatch, 'companyName' | 'city' | 'state' | 'datePosted'>,
    options: FormatJobMetaOptions,
    now: Date = new Date()
): string {
    const location = [job.city, job.state].filter((v): v is string => Boolean(v)).join(', ');
    const parts = [job.companyName, location || null, PostedSegment(job, options.posted, now)].filter((v): v is string => Boolean(v));
    return parts.join(' · ');
}

/**
 * "Also in A; B; C +2 more" — the syndicated-posting line under the card's
 * location, or null when the posting has no other locations to show. The
 * first three carry the full names, semicolon-joined per the mockup's card
 * line format; a fourth-and-beyond is summarized rather than listed (global
 * constraints: card line format).
 */
/**
 * The card's two-line description (CSV 2026-10-24, Job Finder row): the
 * posting's own `descriptionSnippet` with its whitespace runs collapsed, or
 * null when there is nothing to show — no element, no gap.
 */
export function JobCardSnippet(descriptionSnippet: string): string | null {
    const collapsed = descriptionSnippet.replace(/\s+/g, ' ').trim();
    return collapsed.length > 0 ? collapsed : null;
}

export function OtherLocationsLabel(list: readonly string[]): string | null {
    if (list.length === 0) return null;
    const head = list.slice(0, 3).join('; ');
    return list.length > 3 ? `Also in ${head} +${list.length - 3} more` : `Also in ${head}`;
}

/**
 * The card's subtitle line: the employment type as plain text ahead of the org line — the mockup's
 * `j.type + ' · ' + j.org` (jobs.html:628), never a chip (user ruling 2026-10-05). The type goes
 * through `CanonicalEmploymentType`, so a raw feed token (`OTHER`, `FULL_TIME`, `Plein temps`)
 * never reaches the card: a known one reads `Full-time`, an unknown one is left out.
 */
export function JobCardMetaLine(job: Pick<ResearchJobMatch, 'employmentType' | 'companyName' | 'city' | 'state' | 'datePosted'> & Partial<Pick<ResearchJobMatch, 'remote'>>, now: Date = new Date()): string {
    // A remote role says so in the org line, where the mockup's location goes (`org:"Global Modeling
    // Lab · Remote"`, jobs.html:579) — never as a chip (P-J6, 2026-10-08).
    let located: Pick<ResearchJobMatch, 'companyName' | 'city' | 'state' | 'datePosted'> = job;
    if (job.remote === true) {
        const where = [job.city, job.state].filter((v): v is string => Boolean(v)).join(', ');
        located = { ...job, city: where ? `${where} · Remote` : 'Remote', state: null };
    }
    const meta = FormatJobMeta(located, { posted: 'compact' }, now);
    return [CanonicalEmploymentType(job.employmentType), meta || null].filter((v): v is string => Boolean(v)).join(' · ');
}

/**
 * A job result card: wraps `agu-item-card` with the job-specific meta line
 * (employment type · company · location or Remote · posted), the employer-type tag, the
 * recommendation reason ([why]) and a salary line + View role action.
 * `Save`/`Pin`/`Dismiss`/`Open` relay the underlying item-card events under
 * job-domain names, matching `GrantCardComponent`/`PersonCardComponent`'s
 * shape (Tasks 16-17). "Visa sponsored" is deliberately not rendered — spec
 * D7, no data on `ResearchJobMatch`.
 */
@Component({
    standalone: false,
    selector: 'ra-job-card',
    templateUrl: './job-card.component.html',
    styleUrl: './job-card.component.scss',
})
export class JobCardComponent implements OnChanges {
    @Input({ required: true }) Job!: ResearchJobMatch;
    @Input() Recommended = false;
    /** Every card carries the pin in the mockup, on both tabs (parity/jobs.md §4.8 row 5a); the Saved page opts out. */
    /** The title's heading level: 2 on the Saved board (P/for-you.html:947), 3 in the finders. */
    @Input() TitleLevel: 2 | 3 = 3;
    @Input() ShowPin = true;
    @Input() ShowDismiss = true;

    @Output() Save = new EventEmitter<boolean>();
    @Output() Pin = new EventEmitter<boolean>();
    @Output() Dismiss = new EventEmitter<void>();
    @Output() Open = new EventEmitter<void>();

    /**
     * Computed once per `Job` change, not a template-bound getter —
     * `FormatJobAge` defaults `now` to `new Date()`, which a getter would
     * re-evaluate on every change-detection pass (ui-best-practices §7's
     * "never bind time-dependent calls in templates").
     */
    MetaLine = '';

    /**
     * Computed alongside `MetaLine`, not a template getter — same zoneless
     * rule (ui-best-practices §7: no per-pass derivation in the template).
     */
    AlsoIn: string | null = null;

    /**
     * Computed in `ngOnChanges`, cleared by `OnLogoError` — never a template
     * getter (ui-best-practices §7). `null` means "show `CompanyInitials`
     * instead", whether because the feed never gave a logo or because the one
     * it gave 404'd (Review Focus 2: one `error` event swaps to initials and
     * the `<img>` is gone for good, no reload loop).
     */
    LogoUrl: string | null = null;

    /** Computed alongside `LogoUrl` — the avatar fallback shown when there is no logo (or once it 404s). */
    CompanyInitials = '';

    /**
     * Computed alongside `LogoUrl`/`CompanyInitials`, not a template getter
     * (ui-best-practices §7). A getter re-ran `CompanyTypeLabel`/
     * `CanonicalEmploymentType` on every change-detection pass for no reason;
     * worse, re-deriving `LogoUrl` from `this.Job` on EVERY `ngOnChanges` —
     * including one triggered by an unrelated input like `Recommended` —
     * reset a card back to its (still-broken) logo url after `OnLogoError`
     * had already swapped it out, re-arming the "one broken request" contract
     * on every unrelated re-render (fix-round 1 findings #2/#4).
     */
    Tags: readonly string[] = [];

    /** Computed alongside `Tags` (same `changes['Job']` guard); null hides the element. */
    Snippet: string | null = null;

    /** The foot's pay text, computed alongside `Tags` (same `changes['Job']` guard) — never a template getter (ui-best-practices §7). */
    SalaryLine = '';

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['Job']) return;
        this.MetaLine = JobCardMetaLine(this.Job);
        this.AlsoIn = OtherLocationsLabel(this.Job.otherLocations);
        this.LogoUrl = this.Job.companyLogoUrl;
        this.CompanyInitials = Initials(this.Job.companyName);
        this.Tags = this.ComputeTags();
        this.Snippet = JobCardSnippet(this.Job.descriptionSnippet);
        this.SalaryLine = FormatSalaryLine(this.Job);
    }

    /** The logo `<img>`'s `(error)` handler — swaps to initials once, never retries the same broken url. */
    OnLogoError(): void {
        this.LogoUrl = null;
    }

    private ComputeTags(): readonly string[] {
        const tags: string[] = [];
        const companyType = CompanyTypeLabel(this.Job.companyType);
        if (companyType) tags.push(companyType);
        return tags;
    }
}
